/** @jsx jsx */
import { jsx } from '@balance-web/core';
import { forwardRef } from 'react';
import type { FlexProps } from '@balance-web/flex';
import { Text } from '@balance-web/text';
import { useTheme } from '@balance-web/theme';
import type { DisallowStyleProps } from '@balance-web/utils';

import type { BadgeToneColors } from './useToneColor';
import { useBadgeToneColor } from './useToneColor';

type ValidBoxProps = Pick<FlexProps, 'data' | 'id'>;
export type NotificationBadgeProps = {
  /**
   *  Optional cap value for the badge.
   *  When the `value` is greater than the `max` prop, a "+" will automatically be appended.
   */
  max?: number;
  /** The tone of the badge. */
  tone?: BadgeToneColors;
  /** The number displayed inside the badge. */
  value: number;
} & DisallowStyleProps &
  ValidBoxProps;

/** A visual indicator for numeric values such as tallies and scores. */
export const NotificationBadge = forwardRef<
  HTMLDivElement,
  NotificationBadgeProps
>(({ data, id, max, tone, value }, forwardedRef) => {
  const { sizing, spacing, radii } = useTheme();
  const toneColors = useBadgeToneColor({ tone });

  return (
    <Text
      data={data}
      id={id}
      ref={forwardedRef}
      // styles
      css={{
        color: toneColors.foregroundColor,
        backgroundColor: toneColors.backgroundColor,
        display: 'inline-flex',
        fontVariantNumeric: 'tabular-nums lining-nums',
        height: sizing.xsmall,
        lineHeight: sizing.xxsmall,
        padding: `${spacing.xsmall} ${spacing.small}`,
        borderRadius: radii.full,
        alignItems: 'center',
        justifyContent: 'center',
        width: 'fit-content',
      }}
      overflowStrategy="nowrap"
      size="xsmall"
      weight="bold"
    >
      {getBadgeLabel(value, max)}
    </Text>
  );
});

// Utils
// ------------------------------

function getBadgeLabel(value: number, max?: number) {
  if (max && value >= max) {
    return `${max}+`;
  }

  return value.toString();
}
